import { defineMessages } from 'react-intl';

type MessageDescriptor = {
	id: string;
	defaultMessage: string;
	description: string;
};

/**
 * Note: ALL messages in this folder and file should be marked as `.non-final` so that it is not translated
 */
export const messages: {
	tier1HelperText: MessageDescriptor;
	tier2HelperText: MessageDescriptor;
	tier3HelperText: MessageDescriptor;
	title: MessageDescriptor;
	description: MessageDescriptor;
	instructionsIntro: MessageDescriptor;
	instructionsTier1: MessageDescriptor;
	instructionsTier2: MessageDescriptor;
	instructionsTier3: MessageDescriptor;
	tier1TabLabel: MessageDescriptor;
	tier2TabLabel: MessageDescriptor;
	tier3TabLabel: MessageDescriptor;
	resetButton: MessageDescriptor;
	closePanelLabel: MessageDescriptor;
	datasetSmallOrgLabel: MessageDescriptor;
	datasetSmallOrgDescription: MessageDescriptor;
	datasetMediumOrgLabel: MessageDescriptor;
	datasetMediumOrgDescription: MessageDescriptor;
	datasetLargeOrgLabel: MessageDescriptor;
	datasetLargeOrgDescription: MessageDescriptor;
	datasetTier1PowerUserLabel: MessageDescriptor;
	datasetTier1PowerUserDescription: MessageDescriptor;
	datasetTier1EngineerICLabel: MessageDescriptor;
	datasetTier1EngineerICDescription: MessageDescriptor;
	datasetTier1DesignerICLabel: MessageDescriptor;
	datasetTier1DesignerICDescription: MessageDescriptor;
	datasetTier1ProductLeadLabel: MessageDescriptor;
	datasetTier1ProductLeadDescription: MessageDescriptor;
	datasetTier1NewContributorLabel: MessageDescriptor;
	datasetTier1NewContributorDescription: MessageDescriptor;
	datasetTier3BrandNewLabel: MessageDescriptor;
	datasetTier3BrandNewDescription: MessageDescriptor;
	datasetTier3TrialLabel: MessageDescriptor;
	datasetTier3TrialDescription: MessageDescriptor;
	datasetTier3SoloLabel: MessageDescriptor;
	datasetTier3SoloDescription: MessageDescriptor;
	sentryTestSectionTitle: MessageDescriptor;
	sentryTestSectionHelper: MessageDescriptor;
	sentryTestButtonLabel: MessageDescriptor;
	sentryBoundaryButtonLabel: MessageDescriptor;
	sentryBoundaryFallbackText: MessageDescriptor;
	sentryBoundaryResetButtonLabel: MessageDescriptor;
	viewToggleSectionTitle: MessageDescriptor;
	switchToLoggedOutViewButtonLabel: MessageDescriptor;
	switchToLoggedInViewButtonLabel: MessageDescriptor;
} = defineMessages({
	tier1HelperText: {
		id: 'twg-microsite.debugPanel.tier1HelperText.non-final',
		defaultMessage:
			'Pick a Tier 1 dataset to preview the user-scoped view. The home page will render the user graph view seeded with the selected user-metrics payload.',
		description:
			'Helper text shown above the Tier 1 dataset list in the Debug Panel. Here, "graph" means a connected data structure of entities and relationships, as in computer science, not a chart, plot, graphic, or visualization.',
	},
	tier2HelperText: {
		id: 'twg-microsite.debugPanel.tier2HelperText.non-final',
		defaultMessage:
			'Pick a Tier 2 dataset to preview the org-scoped view. The home page will render the org graph view seeded with the selected organisation metrics payload.',
		description:
			'Helper text shown above the Tier 2 dataset list in the Debug Panel. Here, "graph" means a connected data structure of entities and relationships, as in computer science, not a chart, plot, graphic, or visualization.',
	},
	tier3HelperText: {
		id: 'twg-microsite.debugPanel.tier3HelperText.non-final',
		defaultMessage:
			'Pick a Tier 3 scenario to preview the getting-started view. The home page will render the capability showcase with no org metrics data.',
		description: 'Helper text shown above the Tier 3 dataset list in the Debug Panel',
	},
	title: {
		id: 'twg-microsite.debugPanel.title.non-final',
		defaultMessage: 'Data Simulation',
		description: 'Main heading for the Debug Panel',
	},
	description: {
		id: 'twg-microsite.debugPanel.description.non-final',
		defaultMessage:
			'This section allows you to simulate data for different scenarios as different organisations have varying levels of graph density and breadth.',
		description:
			'Description text explaining what the Debug Panel does. Here, "graph" means a connected data structure of entities and relationships, as in computer science, not a chart, plot, graphic, or visualization.',
	},
	instructionsIntro: {
		id: 'twg-microsite.debugPanel.instructionsIntro.non-final',
		defaultMessage: 'Choose a tier to preview:',
		description: 'Introductory sentence before listing tier options in the Debug Panel',
	},
	instructionsTier1: {
		id: 'twg-microsite.debugPanel.instructionsTier1.non-final',
		defaultMessage: 'Tier 1 — user-scoped view seeded from a simulated user-metrics payload.',
		description: 'List item describing the Tier 1 preview in the Debug Panel instructions',
	},
	instructionsTier2: {
		id: 'twg-microsite.debugPanel.instructionsTier2.non-final',
		defaultMessage:
			'Tier 2 — org-scoped view seeded from a simulated organisation-metrics payload.',
		description: 'List item describing the Tier 2 preview in the Debug Panel instructions',
	},
	instructionsTier3: {
		id: 'twg-microsite.debugPanel.instructionsTier3.non-final',
		defaultMessage: 'Tier 3 — getting-started capability showcase for early-days tenants.',
		description: 'List item describing the Tier 3 preview in the Debug Panel instructions',
	},
	tier1TabLabel: {
		id: 'twg-microsite.debugPanel.tier1TabLabel.non-final',
		defaultMessage: 'Tier 1',
		description:
			'Tab label for Tier 1 (user-scoped) view previews in the Debug Panel tier switcher',
	},
	tier2TabLabel: {
		id: 'twg-microsite.debugPanel.tier2TabLabel.non-final',
		defaultMessage: 'Tier 2',
		description: 'Tab label for Tier 2 (org-scoped) view previews in the Debug Panel tier switcher',
	},
	tier3TabLabel: {
		id: 'twg-microsite.debugPanel.tier3TabLabel.non-final',
		defaultMessage: 'Tier 3',
		description:
			'Tab label for Tier 3 (getting started) view previews in the Debug Panel tier switcher',
	},
	resetButton: {
		id: 'twg-microsite.debugPanel.resetButton.non-final',
		defaultMessage: 'Reset',
		description: 'Button label that reloads the page and resets the Debug Panel state',
	},
	closePanelLabel: {
		id: 'twg-microsite.debugPanel.closePanelLabel.non-final',
		defaultMessage: 'Close debug panel',
		description: 'Accessibility label for the debug panel close control',
	},
	datasetSmallOrgLabel: {
		id: 'twg-microsite.debugPanel.dataset.smallOrg.label.non-final',
		defaultMessage: 'Small Org',
		description: 'Label for the small static dataset option in the Debug Panel',
	},
	datasetSmallOrgDescription: {
		id: 'twg-microsite.debugPanel.dataset.smallOrg.description.non-final',
		defaultMessage: 'Small tenant with a modest graph and focused activity.',
		description:
			'Description for the small static dataset option in the Debug Panel. Here, "graph" means a connected data structure of entities and relationships, as in computer science, not a chart, plot, graphic, or visualization.',
	},
	datasetMediumOrgLabel: {
		id: 'twg-microsite.debugPanel.dataset.mediumOrg.label.non-final',
		defaultMessage: 'Medium Org',
		description: 'Label for the medium static dataset option in the Debug Panel',
	},
	datasetMediumOrgDescription: {
		id: 'twg-microsite.debugPanel.dataset.mediumOrg.description.non-final',
		defaultMessage: 'Mid-size tenant with heavy Jira usage and strong code linkage.',
		description:
			'Description for the medium static dataset option in the Debug Panel. "Jira" is an Atlassian product name.',
	},
	datasetLargeOrgLabel: {
		id: 'twg-microsite.debugPanel.dataset.largeOrg.label.non-final',
		defaultMessage: 'Large Org',
		description: 'Label for the large static dataset option in the Debug Panel',
	},
	datasetLargeOrgDescription: {
		id: 'twg-microsite.debugPanel.dataset.largeOrg.description.non-final',
		defaultMessage:
			'Very large tenant with the densest graph and highest activity across products.',
		description:
			'Description for the large static dataset option in the Debug Panel. Here, "graph" means a connected data structure of entities and relationships, as in computer science, not a chart, plot, graphic, or visualization.',
	},
	datasetTier1PowerUserLabel: {
		id: 'twg-microsite.debugPanel.dataset.tier1.powerUser.label.non-final',
		defaultMessage: 'Power user',
		description: 'Label for the Tier 1 "power user" static dataset in the Debug Panel',
	},
	datasetTier1PowerUserDescription: {
		id: 'twg-microsite.debugPanel.dataset.tier1.powerUser.description.non-final',
		defaultMessage: 'Top of the org across pages, comments, work items, videos, and code.',
		description: 'Description for the Tier 1 "power user" static dataset in the Debug Panel',
	},
	datasetTier1EngineerICLabel: {
		id: 'twg-microsite.debugPanel.dataset.tier1.engineerIC.label.non-final',
		defaultMessage: 'Engineer (IC)',
		description: 'Label for the Tier 1 "engineer IC" static dataset in the Debug Panel',
	},
	datasetTier1EngineerICDescription: {
		id: 'twg-microsite.debugPanel.dataset.tier1.engineerIC.description.non-final',
		defaultMessage: 'Heavy code activity — PRs, Jira links, and GitHub events dominate.',
		description:
			'Description for the Tier 1 "engineer IC" static dataset in the Debug Panel. "PRs" means pull requests, "Jira" is an Atlassian product name, and "GitHub" is a third-party developer platform name.',
	},
	datasetTier1DesignerICLabel: {
		id: 'twg-microsite.debugPanel.dataset.tier1.designerIC.label.non-final',
		defaultMessage: 'Designer (IC)',
		description: 'Label for the Tier 1 "designer IC" static dataset in the Debug Panel',
	},
	datasetTier1DesignerICDescription: {
		id: 'twg-microsite.debugPanel.dataset.tier1.designerIC.description.non-final',
		defaultMessage: 'Design-heavy profile — strong Figma, Loom, and page activity.',
		description:
			'Description for the Tier 1 "designer IC" static dataset in the Debug Panel. "Figma" is a third-party design product name, and "Loom" is an Atlassian product name.',
	},
	datasetTier1ProductLeadLabel: {
		id: 'twg-microsite.debugPanel.dataset.tier1.productLead.label.non-final',
		defaultMessage: 'Product lead',
		description: 'Label for the Tier 1 "product lead" static dataset in the Debug Panel',
	},
	datasetTier1ProductLeadDescription: {
		id: 'twg-microsite.debugPanel.dataset.tier1.productLead.description.non-final',
		defaultMessage: 'Lots of pages, comments, and work items; moderate code and design.',
		description: 'Description for the Tier 1 "product lead" static dataset in the Debug Panel',
	},
	datasetTier1NewContributorLabel: {
		id: 'twg-microsite.debugPanel.dataset.tier1.newContributor.label.non-final',
		defaultMessage: 'New contributor',
		description: 'Label for the Tier 1 "new contributor" static dataset in the Debug Panel',
	},
	datasetTier1NewContributorDescription: {
		id: 'twg-microsite.debugPanel.dataset.tier1.newContributor.description.non-final',
		defaultMessage: 'Low volume across all surfaces but just above the Tier 1 floor.',
		description: 'Description for the Tier 1 "new contributor" static dataset in the Debug Panel',
	},
	datasetTier3BrandNewLabel: {
		id: 'twg-microsite.debugPanel.dataset.tier3.brandNewTenant.label.non-final',
		defaultMessage: 'Brand new tenant',
		description: 'Label for the Tier 3 "brand new tenant" static dataset in the Debug Panel',
	},
	datasetTier3BrandNewDescription: {
		id: 'twg-microsite.debugPanel.dataset.tier3.brandNewTenant.description.non-final',
		defaultMessage: 'Freshly provisioned org — nothing connected, no graph activity yet.',
		description:
			'Description for the Tier 3 "brand new tenant" static dataset in the Debug Panel. Here, "graph" means a connected data structure of entities and relationships, as in computer science, not a chart, plot, graphic, or visualization.',
	},
	datasetTier3TrialLabel: {
		id: 'twg-microsite.debugPanel.dataset.tier3.trialTenant.label.non-final',
		defaultMessage: 'Trial tenant',
		description: 'Label for the Tier 3 "trial tenant" static dataset in the Debug Panel',
	},
	datasetTier3TrialDescription: {
		id: 'twg-microsite.debugPanel.dataset.tier3.trialTenant.description.non-final',
		defaultMessage: 'Evaluation tenant with a handful of seats; below the Tier 2 scale gate.',
		description: 'Description for the Tier 3 "trial tenant" static dataset in the Debug Panel',
	},
	datasetTier3SoloLabel: {
		id: 'twg-microsite.debugPanel.dataset.tier3.soloTenant.label.non-final',
		defaultMessage: 'Solo tenant',
		description: 'Label for the Tier 3 "solo tenant" static dataset in the Debug Panel',
	},
	datasetTier3SoloDescription: {
		id: 'twg-microsite.debugPanel.dataset.tier3.soloTenant.description.non-final',
		defaultMessage:
			'Single-user tenant — not enough activity to meet the Tier 2 relationships gate.',
		description: 'Description for the Tier 3 "solo tenant" static dataset in the Debug Panel',
	},
	sentryTestSectionTitle: {
		id: 'twg-microsite.debugPanel.sentryTestSectionTitle.non-final',
		defaultMessage: 'Sentry',
		description:
			'Section header for the Sentry test-exception controls in the Debug Panel. "Sentry" is a third-party application monitoring product name.',
	},
	sentryTestSectionHelper: {
		id: 'twg-microsite.debugPanel.sentryTestSectionHelper.non-final',
		defaultMessage:
			'Throws a test exception and reports it to Sentry via logException. Sentry is disabled in dev environments, so events only appear in the Sentry UI on staging or production.',
		description:
			'Helper text explaining what the Sentry test-exception button does. "Sentry" is a third-party application monitoring product name, and "logException" is a code identifier.',
	},
	sentryTestButtonLabel: {
		id: 'twg-microsite.debugPanel.sentryTestButtonLabel.non-final',
		defaultMessage: 'Trigger test exception',
		description: 'Label for the button that throws a test exception and reports it to Sentry',
	},
	sentryBoundaryButtonLabel: {
		id: 'twg-microsite.debugPanel.sentryBoundaryButtonLabel.non-final',
		defaultMessage: 'Throw render error (boundary)',
		description:
			'Label for the button that throws an error during render to be caught by SentryErrorBoundary',
	},
	sentryBoundaryFallbackText: {
		id: 'twg-microsite.debugPanel.sentryBoundaryFallbackText.non-final',
		defaultMessage: 'SentryErrorBoundary caught a render error and reported it.',
		description:
			'Text shown in the SentryErrorBoundary fallback after a synthetic render error. "SentryErrorBoundary" is a code identifier, the name of a React component. "Sentry" is a third-party error-monitoring product name.',
	},
	sentryBoundaryResetButtonLabel: {
		id: 'twg-microsite.debugPanel.sentryBoundaryResetButtonLabel.non-final',
		defaultMessage: 'Reset boundary',
		description: 'Label for the button that resets the SentryErrorBoundary fallback',
	},
	viewToggleSectionTitle: {
		id: 'twg-microsite.debugPanel.viewToggleSectionTitle.non-final',
		defaultMessage: 'Authentication view',
		description:
			'Section header for the logged-in / logged-out view toggle controls in the Debug Panel.',
	},
	switchToLoggedOutViewButtonLabel: {
		id: 'twg-microsite.debugPanel.switchToLoggedOutViewButtonLabel.non-final',
		defaultMessage: 'Switch to logged out view',
		description:
			'Button label that forces the home page to render the logged-out (marketing) view even when the user is authenticated',
	},
	switchToLoggedInViewButtonLabel: {
		id: 'twg-microsite.debugPanel.switchToLoggedInViewButtonLabel.non-final',
		defaultMessage: 'Switch to logged in view',
		description:
			'Button label that restores the authenticated logged-in view after the debug panel forced the logged-out view',
	},
});
